import type { ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/app/auth-context';
import { PageLoading } from '@/components/feedback/page-loading';
import { Button } from '@/components/ui';
import { routes } from '@/lib/routes';
import { safeReturnTo } from '@/lib/routes';

export function ProtectedRoute({
  children,
  requireOnboarding = false,
  completedOnboardingRedirect = false,
}: {
  children: ReactNode;
  requireOnboarding?: boolean;
  completedOnboardingRedirect?: boolean;
}) {
  const { user, isLoading, error, refresh } = useAuth();
  const location = useLocation();
  const { t } = useTranslation('common');

  if (isLoading) return <PageLoading />;
  if (error) {
    return (
      <main className="route-error route-error--standalone">
        <span>
          <AlertCircle size={25} />
        </span>
        <h1>{t('status.sessionError')}</h1>
        <Button onClick={() => void refresh()}>{t('actions.tryAgain')}</Button>
      </main>
    );
  }
  if (!user) {
    if (location.pathname === routes.admin) {
      return <Navigate to={routes.adminLogin} replace />;
    }
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to={`${routes.login}?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }
  if (user.mustChangePassword && location.pathname !== routes.passwordSetup) {
    return <Navigate to={routes.passwordSetup} replace />;
  }
  if (requireOnboarding && !user.onboardingCompleted) {
    const returnTo = `${location.pathname}${location.search}`;
    return (
      <Navigate to={`${routes.onboarding}?returnTo=${encodeURIComponent(returnTo)}`} replace />
    );
  }
  if (completedOnboardingRedirect && user.onboardingCompleted) {
    const returnTo = safeReturnTo(new URLSearchParams(location.search).get('returnTo'));
    return <Navigate to={returnTo} replace />;
  }
  return children;
}

export function AdminRoute({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (!user) return <Navigate to={routes.adminLogin} replace />;
  return user.role === 'admin' ? children : <Navigate to={routes.app} replace />;
}
